// @nx/rollup builds its own TypeScript program and drops tsconfig's `files`.
// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="./ambient.d.ts" />

import type { FunctionComponent, SVGProps } from 'react'
import { ReactComponent as PlusIconSvg } from './assets/images/plus-icon.svg'
import heartImage from './assets/images/heart.png'
import smileImage from './assets/images/smile.png'
import expiredSessionImage from './assets/images/expired-session.svg'

export { AccountSelector } from './components/account-selector'
export type { AccountSelectorProps } from './components/account-selector/account-selector.types'
export { Accordion } from './components/accordion'
export { AccordionMultiSelect } from './components/accordion-multi-select'
export type {
  AccordionMultiSelectProps,
  AccordionMultiSelectOptions,
} from './components/accordion-multi-select/accordion-multi-select.types'
export { Alert } from './components/alert'
export type { AlertVariant } from './components/alert'
export { Avatar } from './components/avatar'
export { LemonadeBarChart } from './components/bar-chart'
export type {
  LemonadeBarChartProps,
  BarChartSeries,
} from './components/bar-chart/bar-chart.types'
export { ChartTooltipBody } from './components/chart-tooltip'
export type { ChartTooltipRenderProps } from './components/chart-tooltip'
export { LemonadeDonutChart } from './components/donut-chart'
export type { LemonadeDonutChartProps } from './components/donut-chart/donut-chart.types'
export { LemonadeAreaChart } from './components/area-chart'
export type {
  LemonadeAreaChartProps,
  AreaChartSeries,
  AreaChartAxis,
} from './components/area-chart/area-chart.types'
export { LemonadeLineChart } from './components/line-chart'
export type {
  LemonadeLineChartProps,
  LineChartSeries,
  LineChartAxis,
} from './components/line-chart/line-chart.types'
export { Badge } from './components/badge'
export type { BadgeVariant, BadgeIcon, BadgeProps } from './components/badge'
export { Button } from './components/button'
export { ButtonIcon } from './components/button-icon'
export type {
  ButtonIconProps,
  ButtonIconVariant,
} from './components/button-icon'
export { ButtonText } from './components/button-text'
export { Feedback } from './components/feedback'
export { FileInput } from './components/file-input'
export { FlagAvatar } from './components/flag-avatar'
export { Checkbox } from './components/checkbox'
export { FakeCheckbox } from './components/fake-checkbox'
export { Switch } from './components/switch'
export type { SwitchProps } from './components/switch'
export { SegmentedControl } from './components/segmented-control'
export type { SegmentedControlProps } from './components/segmented-control/segmented-control.types'
export { GroupedMultiSelect } from './components/grouped-multi-select'
export type {
  GroupedMultiSelectProps,
  GroupedMultiSelectOptions,
} from './components/grouped-multi-select/grouped-multi-select.types'
export { FormField } from './components/form-field'
export { ColorSwatch } from './components/color-swatch'
export type { ColorSwatchProps } from './components/color-swatch/color-swatch.types'
export { Group } from './components/group'
export { Heading } from './components/heading'
export { Icon, IconRegistry } from './components/icon'
export type { IconProps } from './components/icon'
export { IllustratedMessage } from './components/illustrated-message'
export { InsightsSection } from './components/insights-section'
export type {
  InsightsSectionProps,
  InsightsSectionAction,
} from './components/insights-section/insights-section.types'
export { Label } from './components/label'
export { LanguagePicker } from './components/language-picker'
export type {
  LanguagePickerProps,
  LanguageOption,
} from './components/language-picker'
export { Logo } from './components/logo'
export { LemonadeProvider } from './components/lemonade-provider'
export { Menu, type SectionItem, type SectionProps } from './components/menu'
export {
  MultiStepForm,
  MultiStepFormHeader,
} from './components/multi-step-form'
export type {
  FieldType as MultiStepFormFieldType,
  FormStep as MultiStepFormStep,
  optionType as MultiStepFormOptionType,
} from './components/multi-step-form'
export {
  Select,
  AsyncSelect,
  EndlessAsyncSelect,
  MultiSelect,
  type SelectProps,
} from './components/select'
export type {
  SelectOptionGroup,
  SelectOption,
} from './components/select/option.types'
export type { OptionsFilter } from '@mantine/core'
export { SearchInput } from './components/search-input'
export { Spacer } from './components/spacer'
export { Spinner } from './components/spinner'
export { Chip } from './components/chip'
export { StrenghtMeter } from './components/strenght-meter'
export { Pagination, PaginationItem } from './components/pagination'
export {
  Tabs,
  Tab,
  MountableTab,
  type TabProps,
  type TabItemProps,
} from './components/tabs'
export { Text } from './components/text'
export { TextArea } from './components/text-area'
export { TextInput } from './components/text-input'
export { theme } from './theme/theme'
export { useToast } from './components/toast'
export { Tooltip } from './components/tooltip'
export type { TooltipProps, TooltipOptions } from './components/tooltip'
export { CardDashboard } from './components/card-dashboard'
export { CardContent } from './components/card-content'
export { AvatarInput } from './components/avatar-input'
export { Modal, type ModalProps } from './components/modal'
export { ModalTitle } from './components/modal-title'
export { ModalHeader } from './components/modal-header'
export { ModalContent } from './components/modal-content'
export { ModalFooter } from './components/modal-footer'
export { ConfirmationModal } from './components/confirmation-modal'
export { ConfirmationModal as ActionModal } from './components/confirmation-modal'
export { ConfirmationResultModal } from './components/confirmation-result-modal'
export {
  AcknowledgeModal,
  SuccessIcon,
  ErrorIcon,
} from './components/acknowledge-modal'
export type { AcknowledgeModalProps } from './components/acknowledge-modal'
export { Gallery } from './components/gallery'
export { MaintenancePage } from './components/maintenance-page'
export { Loader } from './components/loader'
export { Radio } from './components/radio'
export {
  PasswordCriteria,
  usePasswordCriteria,
} from './components/password-criteria'
export type { IPasswordCriteriaDefinition } from './components/password-criteria'

export { Filter, FILTER_DIVIDER } from './components/filter'
export { FilterMulti } from './components/filter-multi'
export type {
  FilterMultiProps,
  FilterMultiOption,
} from './components/filter-multi'
export { FilterList, SearchFilterList } from './components/filter-list'
export type { FilterListOption } from './components/filter-list/search-filter-list.types'
export { FilterRange } from './components/filter-range'
export type {
  FilterRangeProps,
  FilterRangeValue,
} from './components/filter-range'
export { ProgressBar } from './components/progress-bar'
export {
  RichDescription,
  sanitizeRichDescription,
} from './components/rich-description'
export { Card } from './components/card'
export { Carousel } from './components/carousel'
export { FullPageFormLayout } from './components/full-page-form-layout'
export {
  AnimatedContainer,
  AnimationType,
} from './components/animated-container'
export { Dropzone } from './components/dropzone'
export type {
  DropzoneProps,
  DropzoneRejection,
  DropzoneRejectionCode,
} from './components/dropzone'
export {
  DocumentsGallery,
  GalleryPreview,
} from './components/documents-gallery'
export {
  TableHeader,
  Table,
  TableBody,
  TablePagination,
  TableSearchAndFilters,
  TableCell,
  TableRow,
  TableProvider,
} from './components/table'
export type {
  TableColumn,
  TableColumnSortDirection,
} from './components/table/table.types'
export { Banner } from './components/banner'
export { Timeline } from './components/timeline'
export type { TimelineElement } from './components/timeline'
export { Anchor } from './components/anchor'
export { Flex } from './components/flex'
export { Grid } from './components/grid'
export {
  Navigation,
  NAVIGATION_WIDTH,
  NAVIGATION_COLLAPSED_WIDTH,
} from './components/navigation'
export type {
  NavItemProps,
  NavItemWithChildrenProps,
  NavItemBadge,
} from './components/navigation/navigation.types'
export { Copy } from './components/copy'
export { CopyableValue } from './components/copyable-value'
export type { CopyableValueProps } from './components/copyable-value'
export { PhoneInput } from './components/phone-input/phone-input'
export { Drawer, useDisclosure } from './components/drawer'
export { Typography } from './components/typography'
export type { TypographyVariant } from './components/typography'
export { DateRange } from './components/date-range'
export { SplitButton } from './components/split-button'
export { UserAvatar } from './components/user-avatar'
export { ExternalLink } from './components/external-link'
export { HelpLinks } from './components/help-links'
export { Breadcrumbs } from './components/breadcrumbs'
export { MarkdownPreview } from './components/markdown-preview'
export { OrganisationHeaderWithImage } from './components/organisation-header-with-image'
export { MessageInput } from './components/message-input'
export {
  MoneyInput,
  CurrencyInput,
  MoneyField,
  CurrencySelector,
} from './components/money-input'
export type {
  MoneyInputProps,
  MoneyFieldProps,
  CurrencyOnlyValue,
  CurrencySelectorProps,
  CurrencyGroup,
  CurrencyList,
} from './components/money-input'
export {
  EmblaCarousel,
  type EmblaCarouselProps,
  type Embla,
} from './components/embla-carousel'
export { MultiSlideCarousel } from './components/multi-slide-carousel/multi-slide-carousel'
export { Box } from './components/box'
export { Divider } from './components/divider'
export { Skeleton } from './components/skeleton'
export { ErrorBoundaryFallback } from './components/error-boundary-fallback'
export type { ErrorBoundaryFallbackProps } from './components/error-boundary-fallback'
export type { InputStatus } from './shared/input-status'
export { getInputStatus } from './shared/input-status'

export type { Theme, Color, Size, BorderRadius } from './theme/theme'

// New token system
export { tokens, primitives } from './theme/tokens'
export type {
  Tokens,
  SemanticColors,
  Spacing,
  Radius,
  Typography as TypographyTokenMap,
  TypographyToken,
} from './theme/tokens'
export {
  // Primitives (layer 1)
  colorScale,
  primaryScale,
  neutralScale,
  successScale,
  warningScale,
  errorScale,
  spacingScale,
  radiusScale,
  // Semantic (layer 2)
  semanticColors,
  spacing,
  radius,
  typography,
  fontFamily,
  fontWeight,
  heights,
} from './theme/tokens'
export { StatCard } from './components/stat-card'
export type {
  StatCardProps,
  StatCardTrend,
} from './components/stat-card/stat-card.types'
export { Stepper, StepperStep } from './components/stepper'
export {
  AssigneePicker,
  type Assignee,
  type PickerStatus,
} from './components/assigneePicker'
export { SidePanel } from './components/side-panel'
export { LottieWrapper } from './components/LottieWrapper/LottieWrapper'
export { MarkdownTextArea } from './components/markdown-text-area'
export { DateInput, type DateInputProps } from './components/date-input'
export { TopBar } from './components/top-bar'
export type {
  TopBarProps,
  TopBarBreadcrumbItem,
  TopBarAccountSelectorProps,
} from './components/top-bar'

// ASSETS
// Explicitly typed: a bare re-export puts the asset path in index.d.ts.
export const PlusIcon: FunctionComponent<
  SVGProps<SVGSVGElement> & { title?: string }
> = PlusIconSvg
export const HeartImage: string = heartImage
export const SmileImage: string = smileImage
export const ExpiredSessionImage: string = expiredSessionImage
